import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  InputDate,
  InputNumber,
  InputText,
  InputTextarea,
  Modal,
  RadioGroup,
  Tabs,
} from '@teo/components';
import {
  Check,
  CrossFilled,
  Search,
  TaskCheckmark,
} from '@teo/components/icons';
import { format } from 'date-fns';
import { axios } from '@/axios';
import { UploadFileBox } from '@/components/UploadDropzoneSection/UploadFileBox';
import { config } from '@/config';
import { useGet } from '@/query/fetchHooks';
import { uploadFile } from '@/query/documents';
import { parseFilter } from '@/util';
import { SparklesIcon, UploadIcon } from '../Competences/SparklesIcon';
import InfoSection from '../InfoSection';

const MAX_CERTIFICATE_SIZE = 100 * 1024 * 1024;
const CERTIFICATE_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];
const RECOGNITION_METHODS = ['manual', 'certificate', 'exemption'];
const HISTORY_SOURCE_KEYS = {
  course: 'course',
  coursecontent: 'course',
  coursecontentprofession: 'course',
  directory: 'directory',
  learningpath: 'learningpath',
  learningpathprofession: 'learningpath',
  lesson: 'lesson',
  training: 'training',
  widget: 'widget',
  widgetattempt: 'widget',
};

function competenceLabel(competence) {
  const parts = competence?.name;
  return parts;
}

export function AddCompetenceUser({
  isOpen,
  onAssigned,
  onClose,
  organisationId,
  studentName,
  userId,
  activeCompetence,
  action = 'assign',
}) {
  const { t, i18n } = useTranslation();
  const competenceSearchRef = useRef(null);
  const [tab, setTab] = useState(action === 'history' ? 'history' : 'details');
  const [selectedCompetence, setSelectedCompetence] = useState([]);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [selectedRecognition, setSelectedRecognition] = useState('manual');
  const [description, setDescription] = useState('');
  const [eventDate, setEventDate] = useState(new Date());
  const [certificateFile, setCertificateFile] = useState(null);
  const [certificateChanged, setCertificateChanged] = useState(false);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [validityDuration, setValidityDuration] = useState('forever');
  const [validForDays, setValidForDays] = useState(90);
  const [expiryDate, setExpiryDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 90);
    return date;
  });
  const activeCertificateUrl = activeCompetence?.certificateUrl;
  const isRevokeAction = action === 'revoke';
  const isHistoryAction = action === 'history';

  useEffect(() => {
    if (!isOpen) return;

    setTab(isHistoryAction ? 'history' : 'details');
    setSelectedRecognition(
      !isRevokeAction &&
        RECOGNITION_METHODS.includes(activeCompetence?.assignmentMethod)
        ? activeCompetence.assignmentMethod
        : 'manual'
    );
    setDescription(
      isRevokeAction ? '' : activeCompetence?.decisionDescription || ''
    );
    setEventDate(
      !isRevokeAction && activeCompetence?.achievedAt
        ? new Date(activeCompetence.achievedAt)
        : new Date()
    );
    setCertificateFile(null);
    setError('');

    if (activeCompetence?.validForDays) {
      setValidForDays(activeCompetence.validForDays);
      setValidityDuration('days');
    } else if (activeCompetence?.expiryDate) {
      setExpiryDate(new Date(activeCompetence.expiryDate));
      setValidityDuration('date');
    } else {
      setValidityDuration('forever');
    }
  }, [activeCompetence, isHistoryAction, isOpen, isRevokeAction]);

  useEffect(() => {
    const loadCertificateFile = async () => {
      if (
        isRevokeAction ||
        isHistoryAction ||
        selectedRecognition !== 'certificate' ||
        !activeCertificateUrl
      ) {
        return;
      }

      try {
        const response = await fetch(activeCertificateUrl);

        if (!response.ok) {
          throw new Error('Failed to load certificate');
        }

        const blob = await response.blob();

        const fileName =
          activeCertificateUrl.split('/').pop()?.split('?')[0] ||
          'certificate.pdf';

        const file = new File([blob], fileName, {
          type: blob.type,
        });

        setCertificateFile(file);
        setCertificateChanged(false);
      } catch (error) {
        console.error('Failed to load certificate:', error);
      }
    };

    loadCertificateFile();
  }, [
    activeCertificateUrl,
    isHistoryAction,
    isRevokeAction,
    selectedRecognition,
  ]);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    if (!showResults) return undefined;

    const handlePointerDown = (event) => {
      if (
        competenceSearchRef.current &&
        !competenceSearchRef.current.contains(event.target)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [showResults]);

  const competencesResult = useGet('/organisationcompetencedetails', {
    organisationId: `${organisationId},OR,organisationId=null`,
    lang: i18n.language,
    disabled: false,
    count: 20,
    sort: 'competenceId',
    q: parseFilter(debouncedQuery),
    enabled: isOpen && !activeCompetence && !!organisationId && !!userId,
  });

  const historyResult = useGet('/studentcompetencehistory', {
    organisationId,
    userId,
    competenceId: activeCompetence?.competenceId,
    lang: i18n.language,
    count: 100,
    sort: '-createdAt,-id',
    enabled:
      isOpen &&
      tab === 'history' &&
      !!activeCompetence?.competenceId &&
      !!organisationId &&
      !!userId,
  });

  const competences = useMemo(
    () => competencesResult?.data?.data || [],
    [competencesResult?.data]
  );
  const history = historyResult?.data?.data || [];

  const expiry = useMemo(() => {
    if (validityDuration === 'forever') return null;
    if (validityDuration === 'date') {
      if (!expiryDate) return undefined;
      const date = new Date(expiryDate);
      date.setHours(23, 59, 59, 999);
      return date;
    }
    if (!Number.isFinite(validForDays) || validForDays < 1) return undefined;
    const date = new Date(eventDate || new Date());
    date.setDate(date.getDate() + validForDays);
    return date;
  }, [eventDate, expiryDate, validForDays, validityDuration]);

  const selectCertificateFile = (file) => {
    if (!file) {
      setCertificateFile(null);
      setError('');
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    if (!CERTIFICATE_EXTENSIONS.includes(extension)) {
      setCertificateFile(null);
      setError(
        t('pages.follow_up_users.manual_assign_certificate_invalid_type')
      );
      return;
    }
    if (file.size > MAX_CERTIFICATE_SIZE) {
      setCertificateFile(null);
      setError(t('pages.follow_up_users.manual_assign_certificate_too_large'));
      return;
    }

    setCertificateFile(file);
    setCertificateChanged(true);
    setError('');
  };

  const handleSubmit = async () => {
    if (
      (selectedCompetence.length < 1 && !activeCompetence?.competenceId) ||
      !eventDate ||
      isSaving
    ) {
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const assignmentMethod = isRevokeAction ? 'revoked' : selectedRecognition;
      let certificateUrl =
        assignmentMethod === 'certificate'
          ? activeCompetence?.certificateUrl || null
          : null;
      if (
        assignmentMethod === 'certificate' &&
        certificateFile &&
        certificateChanged
      ) {
        certificateUrl = await uploadFile(
          `competence-certificates/${Number(
            organisationId
          )}/${userId}/${Date.now()}`,
          certificateFile
        );
      }

      const props = {
        organisationId: Number(organisationId),
        userId,
        assignmentMethod,
        certificateUrl,
        expiryDate:
          !isRevokeAction && expiry && validityDuration === 'date'
            ? expiry.toISOString()
            : null,
        validForDays:
          !isRevokeAction && validityDuration === 'days' ? validForDays : null,
        isAchieved: !isRevokeAction,
        achievedAt: !isRevokeAction ? eventDate.toISOString() : null,
        revokedAt: isRevokeAction ? eventDate.toISOString() : null,
        description,
      };

      const targets = activeCompetence?.competenceId
        ? [activeCompetence]
        : selectedCompetence;
      for (const competence of targets) {
        await axios.post(`${config.backend}/studentcompetences`, {
          competenceId: competence.competenceId,
          ...props,
        });
      }

      document.body.style.overflow = 'unset';
      onAssigned();
      onClose();
    } catch {
      setError(t('pages.follow_up_users.manual_assign_error'));
    } finally {
      setIsSaving(false);
    }
  };

  const recognitionOptions = [
    {
      method: 'manual',
      label: t('pages.follow_up_users.assign_comp_user.manual'),
      Icon: TaskCheckmark,
    },
    {
      method: 'certificate',
      label: t('pages.follow_up_users.assign_comp_user.manual_certificate'),
      Icon: UploadIcon,
    },
    {
      method: 'exemption',
      label: t('pages.follow_up_users.assign_comp_user.exemption'),
      Icon: SparklesIcon,
    },
  ];

  const historyStatusLabel = (entry) => {
    if (entry.isExpired) {
      return t('pages.follow_up_users.profile_status_expired');
    }
    if (entry.assignmentMethod === 'revoked') {
      return t('pages.follow_up_users.competence_status_revoked');
    }
    if (entry.assignmentMethod === 'exemption') {
      return t('pages.follow_up_users.competence_status_exempted');
    }
    if (entry.outcome === 'granted') {
      return t('pages.follow_up_users.competence_status_completed');
    }
    return t('pages.follow_up_users.competence_status_not_completed');
  };

  const isAutomaticHistoryEntry = (entry) =>
    entry.historyType === 'automatic' || !!entry.sourceType;

  const historySourceLabel = (entry) => {
    const sourceKey =
      HISTORY_SOURCE_KEYS[entry.sourceType] ||
      HISTORY_SOURCE_KEYS[entry.assignmentSourceType];

    return sourceKey
      ? t(`pages.follow_up_users.competence_history_source_${sourceKey}`)
      : null;
  };

  const renderCompetence = (competence, removable = false) => (
    <div
      key={competence.competenceId}
      className="flex items-center gap-2 rounded-md border px-3 py-3"
    >
      {competence.competenceCategoryColorCode && (
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: competence.competenceCategoryColorCode }}
        />
      )}
      {competence.code && (
        <span className="text-xs text-grey-06">{competence.code}</span>
      )}
      <span className="min-w-0 flex-1 text-sm">
        {competenceLabel(competence)}
      </span>
      {removable && (
        <Button
          className="!px-2"
          variant="ghost"
          onClick={() =>
            setSelectedCompetence((current) =>
              current.filter(
                (item) => item.competenceId !== competence.competenceId
              )
            )
          }
        >
          <CrossFilled className="h-4 w-4 text-grey-06" />
        </Button>
      )}
    </div>
  );

  const saveDisabled =
    (selectedCompetence.length < 1 && !activeCompetence?.competenceId) ||
    isSaving ||
    !eventDate ||
    (!isRevokeAction &&
      selectedRecognition === 'certificate' &&
      !certificateFile &&
      !activeCompetence?.certificateUrl) ||
    (!isRevokeAction && expiry === undefined);

  return (
    <Modal isOpen={isOpen} className="!max-w-[760px]">
      <Modal.Header onClose={onClose}>
        <div>
          <h2 className="text-base font-semibold">
            {t(
              isHistoryAction
                ? 'pages.follow_up_users.competence_history_tab'
                : isRevokeAction
                ? 'pages.follow_up_users.competence_revoke_title'
                : 'pages.follow_up_users.assign_comp_user.title'
            )}
          </h2>
          <p className="mt-0.5 text-xs font-normal text-grey-06">
            {t('pages.follow_up_users.manual_assign_for_user', {
              student: studentName,
            })}
          </p>
        </div>
      </Modal.Header>

      <Modal.Body>
        {activeCompetence?.competenceId && !isHistoryAction && (
          <div className="flex flex-col lg:flex-row">
            <Tabs activeName={tab} onChange={setTab}>
              <Tabs.TabButton name="details">
                <p className="text-base font-medium">
                  {t('pages.follow_up_users.competence_details_tab')}
                </p>
              </Tabs.TabButton>
              <Tabs.TabButton name="history">
                <p className="text-base font-medium">
                  {t('pages.follow_up_users.competence_history_tab')}
                </p>
              </Tabs.TabButton>
            </Tabs>
          </div>
        )}

        {tab === 'history' ? (
          <div className="-mx-6 mt-5 max-h-[62vh] overflow-y-scroll px-6">
            {historyResult?.isLoading ? (
              <p className="py-8 text-center text-sm text-grey-06">
                {t('pages.follow_up_users.competence_history_loading')}
              </p>
            ) : history.length === 0 ? (
              <p className="py-8 text-center text-sm text-grey-06">
                {t('pages.follow_up_users.competence_history_empty')}
              </p>
            ) : (
              <>
                <p className="text-sm font-normal text-[#7d7d81]">
                  {t('pages.follow_up_users.competence_history_description', {
                    competence: competenceLabel(activeCompetence),
                  })}
                </p>
                <div className="relative mt-6 mb-4 flex flex-col gap-5">
                  <div className="absolute left-0 top-4 h-[calc(100%_-_32px)] w-0.5 bg-grey-02" />
                  {history.map((entry) => {
                    const isAutomatic = isAutomaticHistoryEntry(entry);
                    const source = isAutomatic
                      ? historySourceLabel(entry)
                      : null;
                    const description = !isAutomatic
                      ? entry.description || entry.denialReason
                      : null;
                    return (
                      <div
                        key={entry.historyId || entry.id}
                        className="relative flex flex-col gap-1 pl-8"
                      >
                        <div className="absolute -left-0.5 top-2/4 -ml-px h-2 w-2 -translate-y-1/2 rounded-full bg-grey-05" />
                        <p className="pl-1.5 text-sm text-grey-06">
                          {format(
                            new Date(entry.updatedAt || entry.createdAt || ''),
                            'dd/MM/yyyy HH:mm'
                          )}
                        </p>
                        <p className="pl-1.5 text-sm text-grey-08">
                          <strong className="font-semibold">
                            {historyStatusLabel(entry)}
                          </strong>
                          {' - '}
                          {t(
                            isAutomatic
                              ? 'pages.follow_up_users.competence_history_origin_automatic'
                              : 'pages.follow_up_users.competence_history_method_manual'
                          )}
                          {source && <> · {source}</>}
                        </p>
                        {description && (
                          <p className="whitespace-pre-wrap pl-1.5 text-sm text-grey-08">
                            {description}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        ) : (
          <div
            className={`-mx-6 max-h-[62vh] overflow-y-scroll px-6 ${
              activeCompetence?.competenceId ? 'mt-5' : ''
            }`}
          >
            <div className="flex flex-col gap-4">
              {activeCompetence ? (
                renderCompetence(activeCompetence)
              ) : (
                <div className="flex flex-col">
                  {selectedCompetence.length > 0 && (
                    <div className="mb-4 flex flex-col gap-2">
                      {selectedCompetence.map((competence) =>
                        renderCompetence(competence, true)
                      )}
                    </div>
                  )}
                  <p className="mb-2 text-sm font-semibold text-black">
                    {t(
                      'pages.follow_up_users.assign_comp_user.what_competence'
                    )}
                  </p>
                  <div ref={competenceSearchRef} className="relative">
                    <InputText
                      Icon={Search}
                      value={query}
                      placeholder={t(
                        'pages.follow_up_users.manual_assign_search'
                      )}
                      onChange={(event) => setQuery(event.target.value)}
                      onFocus={() => setShowResults(true)}
                    />
                    {showResults && competencesResult?.isLoading && (
                      <div className="shadow-m absolute z-20 mt-1 w-full rounded-md border border-grey-03 bg-white px-3 py-2 text-sm text-grey-06">
                        {t('pages.profile_assignment.loading')}
                      </div>
                    )}
                    {showResults &&
                      !competencesResult?.isLoading &&
                      competences.length > 0 && (
                        <div className="shadow-m absolute z-20 mt-1 flex max-h-44 w-full flex-col gap-1 overflow-auto rounded-md border border-grey-03 bg-white p-1">
                          {competences.map((competence) => {
                            const isSelected = selectedCompetence.some(
                              (item) =>
                                item.competenceId === competence.competenceId
                            );
                            return (
                              <button
                                key={competence.competenceId}
                                type="button"
                                className={`flex w-full items-center gap-2 rounded px-2 py-2 text-left hover:bg-grey-01 ${
                                  isSelected ? 'bg-[#fff6d6]' : ''
                                }`}
                                onClick={() => {
                                  setSelectedCompetence((current) =>
                                    isSelected
                                      ? current.filter(
                                          (item) =>
                                            item.competenceId !==
                                            competence.competenceId
                                        )
                                      : [...current, competence]
                                  );
                                  setQuery('');
                                  setShowResults(false);
                                }}
                              >
                                <span className="w-20 shrink-0 text-xs text-grey-06">
                                  {competence.code}
                                </span>
                                <span className="truncate text-sm">
                                  {competenceLabel(competence)}
                                </span>
                                {isSelected && (
                                  <Check className="ml-auto w-5 text-success-05" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                  </div>
                </div>
              )}

              {!isRevokeAction && (
                <div>
                  <p className="mb-2 text-sm font-semibold text-black">
                    {!activeCompetence && '2 · '}
                    {t('pages.follow_up_users.assign_comp_user.recognized')}
                  </p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {recognitionOptions.map(({ method, label, Icon }) => (
                      <button
                        key={method}
                        type="button"
                        aria-pressed={selectedRecognition === method}
                        onClick={() => {
                          setSelectedRecognition(method);
                          setError('');
                        }}
                        className={`flex flex-col items-center gap-2 rounded-lg border px-3 py-4 ${
                          selectedRecognition === method
                            ? 'border-secondary-04'
                            : 'border-grey-02'
                        }`}
                      >
                        <Icon className="h-5 w-5 text-grey-07" />
                        <span className="text-center text-xs font-medium">
                          {label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!isRevokeAction && selectedRecognition === 'exemption' && (
                <InfoSection
                  text={t('pages.follow_up_users.manual_assign_exemption_info')}
                />
              )}

              {!isRevokeAction && selectedRecognition === 'certificate' && (
                <div>
                  <label className="mb-1.5 block text-sm font-semibold">
                    {t(
                      'pages.follow_up_users.manual_assign_certificate_file_label'
                    )}
                  </label>
                  <UploadFileBox
                    files={certificateFile ? [certificateFile] : []}
                    setFiles={(files) =>
                      selectCertificateFile(Array.from(files)[0])
                    }
                    multiple={false}
                    accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  />
                  {activeCompetence?.certificateUrl && !certificateFile && (
                    <p className="mt-1 text-[11px] text-grey-06">
                      {t(
                        'pages.follow_up_users.manual_assign_certificate_existing'
                      )}
                    </p>
                  )}
                  <p className="mt-1 text-[11px] text-grey-06">
                    {t(
                      'pages.follow_up_users.manual_assign_certificate_formats'
                    )}
                  </p>
                </div>
              )}

              <div className="max-w-[220px]">
                <label className="mb-1.5 block text-sm font-semibold">
                  {t(
                    isRevokeAction
                      ? 'pages.follow_up_users.competence_revoked_on'
                      : selectedRecognition === 'exemption'
                      ? 'pages.follow_up_users.competence_exempted_on'
                      : 'pages.follow_up_users.manual_assign_date'
                  )}
                </label>
                <InputDate
                  id="manual-competence-event-date"
                  value={eventDate}
                  onChange={setEventDate}
                />
              </div>

              {!isRevokeAction && (
                <div className="my-2 flex w-full flex-col">
                  <RadioGroup
                    label={t('pages.follow_up_users.expiration_date')}
                    value={validityDuration}
                    gap="gap-2"
                    onChange={setValidityDuration}
                  >
                    <div className="flex flex-col gap-2 sm:flex-row">
                      {['forever', 'days', 'date'].map((duration) => (
                        <div
                          key={duration}
                          className={`flex-1 rounded-lg border border-grey-02 p-3 ${
                            validityDuration === duration
                              ? 'border-secondary-04'
                              : ''
                          }`}
                        >
                          <RadioGroup.Option value={duration}>
                            <span className="text-xs">
                              {t(
                                `pages.follow_up_users.manual_assign_deny_duration_${duration}`
                              )}
                            </span>
                          </RadioGroup.Option>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>

                  {validityDuration === 'days' && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-grey-06">
                      <InputNumber
                        mode="integer"
                        min={1}
                        value={validForDays}
                        className="max-w-[96px]"
                        onChange={(value) => setValidForDays(value ?? 0)}
                      />
                      <span>
                        {t(
                          'pages.follow_up_users.manual_assign_validity_days_expiry',
                          {
                            date: expiry
                              ? new Intl.DateTimeFormat(i18n.language).format(
                                  expiry
                                )
                              : '—',
                          }
                        )}
                      </span>
                    </div>
                  )}

                  {validityDuration === 'date' && (
                    <div className="mt-3 max-w-[220px]">
                      <InputDate
                        id="manual-competence-expiry-date"
                        value={expiryDate}
                        onChange={setExpiryDate}
                      />
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-sm font-semibold">
                  {t(
                    isRevokeAction
                      ? 'pages.follow_up_users.competence_revoke_motivation'
                      : selectedRecognition === 'exemption'
                      ? 'pages.follow_up_users.manual_assign_exemption_reason'
                      : selectedRecognition === 'certificate'
                      ? 'pages.follow_up_users.manual_assign_certificate_notes'
                      : 'pages.follow_up_users.manual_assign_motivation'
                  )}
                </label>
                <InputTextarea
                  value={description}
                  minRows={4}
                  limit={2000}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder={t(
                    isRevokeAction
                      ? 'pages.follow_up_users.competence_revoke_motivation_placeholder'
                      : selectedRecognition === 'exemption'
                      ? 'pages.follow_up_users.manual_assign_exemption_reason_placeholder'
                      : selectedRecognition === 'certificate'
                      ? 'pages.follow_up_users.manual_assign_certificate_notes_placeholder'
                      : 'pages.follow_up_users.manual_assign_motivation_placeholder'
                  )}
                />
                <p className="mt-1 text-[12px] text-grey-06">
                  {t('pages.follow_up_users.manual_assign_motivation_help')}
                </p>
              </div>
              {error && <p className="text-sm text-error-05">{error}</p>}
            </div>
          </div>
        )}
      </Modal.Body>

      {tab === 'details' && (
        <Modal.Footer cancelButtonLabel={t('button.cancel')} onCancel={onClose}>
          <Button disabled={saveDisabled} onClick={handleSubmit}>
            {t(
              isRevokeAction
                ? 'pages.follow_up_users.competence_revoke_save'
                : 'pages.follow_up_users.manual_assign_save'
            )}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}
